import {BehaviorSubject, Observable, of} from 'rxjs';
import UserModel from '../../model/user.model';
import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {catchError, switchMap} from 'rxjs/operators';
import Pagination from '../../model/pagination';
import QueryEncoder from '../queryEncoder';

@Injectable()
export default class UserService {
  private userSubject = new BehaviorSubject<UserModel>(null);
  public user$ = this.userSubject.asObservable();

  get token() {
    return localStorage.getItem('token');
  }

  set token(val: string) {
    if(val == null) {
      localStorage.removeItem('token');
    } else {
      localStorage.setItem('token', val);
    }
  }

  get ssoToken() {
    return localStorage.getItem('sso_token');
  }

  set ssoToken(val: string) {
    if(val == null) {
      localStorage.removeItem('sso_token');
    } else {
      localStorage.setItem('sso_token', val);
    }
  }

  get refreshToken() {
    return localStorage.getItem('refresh_token');
  }

  set refreshToken(val: string) {
    if(val == null) {
      localStorage.removeItem('refresh_token');
    } else {
      localStorage.setItem('refresh_token', val);
    }
  }

  public constructor(private http: HttpClient) {
      this.user$.subscribe(result => {
      });
  }

  public login(username: string, password: string): Observable<boolean> {
    return this.http.post<boolean>(environment.apiUrl + '/login_check',
      {
        username,
        password
    }).pipe(switchMap((result: any) => {
      this.token = result.token;
      this.loadUserInfo().subscribe();
      return of(true);
    }), catchError(err => {
      return of(false);
    }));
  }

  public fetchRefreshToken(): Observable<string> {
    return this.http.get<{refresh_token: string}>(environment.ssoUrl + '/api/user/get-refresh-token')
      .pipe(switchMap(result => of(result.refresh_token)));
  }

  public refreshJwt(): Observable<string> {
    return this.http.post<{ token: string }>(environment.ssoUrl + '/api/user/refresh-jwt', {}, {
      headers: new HttpHeaders({
        'X-Refresh-Token': this.refreshToken,
        'wo-intercept': '1'
      })
    }).pipe(
        switchMap(result => of(result.token)
        ));
  }

  public loadUserInfo(): Observable<UserModel> {
    if(!this.token && !this.ssoToken) {
      return of(null);
    }

    return this.http.get<UserModel>(environment.apiUrl + '/users/info').pipe(
      switchMap(result => {
        this.userSubject.next(result);
        if(this.ssoToken != null) {
          this.fetchRefreshToken().subscribe(refreshToken => {
            this.refreshToken = refreshToken;
          });
        }
        return of(result);
      }),
      catchError(error => {
        console.log('error getting user', error);
        return of(null);
      })
    );
  }

  public ssoConnect(): Observable<{ url: string }> {
    let successUrl = location.href;
    let failureUrl = location.href;
    let q = {
      redirect_success: successUrl,
      redirect_failure: failureUrl
    };

    return this.http.get<{ url: string }>(environment.apiUrl + '/users/connect?' + QueryEncoder.encodeURI(q));
  }

  public getToken() {
    return this.token;
  }

  public logout() {
    this.token = null;
    this.ssoToken = null;
    this.userSubject.next(null);
  }

  public list(query?: string, page?: number, limit?: number, sort?: string, direction?: string, additionalFields?: any): Observable<Pagination<UserModel>> {
    query = query || '';
    page = page || 1;
    limit = limit || 15;
    sort = sort || '';

    let sorting = {};
    sorting[sort] = direction;

    let q = {query, page, limit, sorting};
    if(additionalFields) {
      Object.assign(q, additionalFields);
    }

    return this.http
      .get<Pagination<UserModel>>(environment.apiUrl + '/users?' + QueryEncoder.encodeURI(q));
  }

  public getUserSnapshot(): UserModel {
    return this.userSubject.getValue();
  }

  public update(user: UserModel): Observable<boolean> {
    return this.http.put<boolean>(environment.apiUrl + '/users/' + user.id, user).pipe(switchMap(r => of(true)), catchError(e => of(false)));
  }

  public hasRole(role: string) {
    return this.userSubject.getValue() != null && this.userSubject.getValue().roles.indexOf(role) >= 0;
  }

}
